import { useState } from "react";
import { KeyRound, Loader2, ShieldAlert } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { WordMark } from "@/components/ui/WordMark";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { useAdminAuth } from "@/lib/admin-auth";
import { cn } from "@/lib/cn";

export function AdminLogin() {
  const { status, login } = useAdminAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="flex items-center justify-between px-6 py-5">
        <WordMark />
        <ThemeToggle />
      </header>
      <Container size="narrow" className="flex flex-1 flex-col items-center justify-center py-16">
        <div className="w-full surface-card relative overflow-hidden p-8 sm:p-10">
          <div
            aria-hidden
            className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-accent/12 blur-3xl"
          />
          <p className="eyebrow text-accent inline-flex items-center gap-2">
            <KeyRound className="h-3.5 w-3.5" strokeWidth={1.75} />
            Admin sign-in
          </p>
          <h1 className="display-title mt-3 text-3xl font-light leading-tight">
            Organize a gathering
          </h1>
          <p className="mt-2 text-sm text-muted">
            Enter the admin password to edit events, upload banners, and view submissions.
          </p>

          {status === "unconfigured" ? (
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300/60 bg-amber-50 px-4 py-3 dark:border-amber-700/40 dark:bg-amber-900/20">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" strokeWidth={1.75} />
              <div className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                Admin is not configured yet. Run{" "}
                <code className="font-mono">bun run admin:hash &lt;your-pwd&gt;</code> and add the
                two secrets via{" "}
                <code className="font-mono">wrangler pages secret put</code>.
              </div>
            </div>
          ) : null}

          <form onSubmit={submit} className="mt-6 grid gap-4">
            <label className="block">
              <span className="block text-sm font-medium text-ink">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="••••••••"
              />
            </label>
            {error ? (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            ) : null}
            <button
              type="submit"
              disabled={submitting || !password}
              className={cn(
                "inline-flex items-center justify-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-canvas",
                "dark:bg-accent dark:text-accent-on",
                "shadow-glow transition-all hover:bg-ink/90 dark:hover:bg-accent/90 focus-ring",
                "disabled:opacity-60",
              )}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
              ) : null}
              Sign in
            </button>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-subtle">
          Sessions expire after 8 hours. Passwords never leave the server unhashed.
        </p>
      </Container>
    </div>
  );
}
